import { ErrorMessage, Field, Form, Formik } from "formik";
import { Modal } from "react-bootstrap";
import { FaTimes } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import axios from "axios";
import { toast } from "react-toastify";
import { registerEndUser } from "../../features/user/userSlice";
import { base_url } from "../../utils/axiosConfig";

const EndUserSignUp = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(0);
  const [isRegistered, setIsRegistered] = useState(false);
  const [packages, setPackages] = useState([]);
  const [referredUserData, setReferredUserData] = useState(null);
  const timeoutRef = useRef(null);

  // Debounce function for referral code validation
  const debounce = (func, delay) => {
    return (...args) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => func(...args), delay);
    };
  };

  // Fetch referred user data
  const fetchReferredUserData = async (referralCode) => {
    if (!referralCode) {
      setReferredUserData(null);
      return;
    }
    try {
      const response = await axios.get(
        `${base_url}user/getReferredUserData?referralCode=${referralCode}`
      );
      setReferredUserData(response.data);
    } catch (error) {
      console.error("Error fetching referred user data:", error);
      setReferredUserData(null);
    }
  };

  const debouncedFetchReferredUserData = debounce(fetchReferredUserData, 500);

  // Fetch packages for the commented-out package selection step
  useEffect(() => {
    if (currentStep === 3 && show) {
      axios
        .get(`${base_url}v1/package/fetchAllpackage`)
        .then((response) => {
          setPackages(response.data.data);
        })
        .catch((error) => {
          console.error("Error fetching packages:", error);
        });
    }
  }, [currentStep, show]);

  // Cleanup debounce timeout
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Validation schemas for each step
  const signupSchemas = [
    Yup.object({
      firstname: Yup.string().required("First Name is required"),
      lastname: Yup.string().required("Last Name is required"),
      dob: Yup.date().required("DOB is required"),
      gender: Yup.string().required("Gender is required"),
      bloodGroup: Yup.string().required("Blood Group is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      mobile: Yup.string()
        .matches(/^[0-9]+$/, "Not a Valid Phone Number")
        .required("Phone is required"),
      bloodGroup: Yup.string().required("Blood Group is required"),
      profilePhoto: Yup.mixed().required("Profile photo is required"),
    }),
    Yup.object({
      address: Yup.object({
        address_line: Yup.string().required("Street address is required"),
        city: Yup.string().required("City is required"),
        district: Yup.string().required("District is required"),
        state: Yup.string().required("State is required"),
        country: Yup.string().required("Country is required"),
        pincode: Yup.string()
          .matches(/^[0-9]+$/, "Not a Valid Pincode")
          .required("Pincode is required"),
      }),
    }),
    // Yup.object({
    //   referralCode: Yup.string(),
    // }),
    // Yup.object({
    //   packageId: Yup.string().required("Please select a package"),
    // }),
  ];

  const initialSignupValues = {
    firstname: "",
    lastname: "",
    dob: "",
    gender: "",
    bloodGroup: "",
    email: "",
    mobile: "",
    bloodGroup: "",
    profilePhoto: null,
    govProof: null,
    password: "",
    address: {
      address_line: "",
      district: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
    },
    // referralCode: "",
    packageId: "",
  };

  const steps = ["Personal Information", "Address Details"];

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="modal-90w md:modal-lg"
      backdrop="static"
    >
      <Modal.Body className="p-4 md:p-6">
        <div className="relative bg-white rounded-lg">
          <button
            onClick={onHide}
            className="absolute top-2 right-2 bg-transparent border-0"
          >
            <FaTimes size={16} color="#666" />
          </button>
          {!isRegistered && (
            <div className="text-center mb-4">
              <h3 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#00C70D] to-[#005AD0] bg-clip-text text-transparent">
                User Sign Up
              </h3>
              <p className="text-gray-500 text-xs md:text-sm">
                Fill in your details to sign up as a Member
              </p>
            </div>
          )}
          {isRegistered ? (
            <div className="text-center py-6">
              <h2 className="text-lg md:text-xl font-bold text-green-600">
                Registration Successful
              </h2>
              <svg
                className="w-10 h-10 mx-auto text-green-600 mt-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          ) : (
            <>
              {/* <div className="mb-6">
                <div className="flex justify-between mb-4">
                  {steps.map((step, index) => (
                    <div
                      key={index}
                      className={`text-sm font-semibold ${
                        index <= currentStep ? "text-[#00C70D]" : "text-gray-400"
                      }`}
                    >
                      {step}
                    </div>
                  ))}
                </div>
              </div> */}

              <Formik
                initialValues={initialSignupValues}
                validationSchema={signupSchemas[currentStep]}
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                  if (currentStep < steps.length - 1) {
                    setCurrentStep((prev) => prev + 1);
                    setSubmitting(false);
                  } else {
                    try {
                      const formData = new FormData();
                      formData.append("firstname", values.firstname);
                      formData.append("lastname", values.lastname);
                      formData.append("dob", values.dob);
                      formData.append("gender", values.gender);
                      formData.append("bloodGroup", values.bloodGroup);
                      formData.append("email", values.email);
                      formData.append("mobile", values.mobile);
                      formData.append(
                        "address.address_line",
                        values.address.address_line
                      );
                      formData.append(
                        "address.district",
                        values.address.district
                      );
                      formData.append("address.city", values.address.city);
                      formData.append("address.state", values.address.state);
                      formData.append(
                        "address.country",
                        values.address.country
                      );
                      formData.append(
                        "address.pincode",
                        values.address.pincode
                      );
                      // formData.append("referralCode", values.referralCode);
                      formData.append("profilePhoto", values.profilePhoto);
                      formData.append("userType", "member");
                      // formData.append("govProof", values.govProof);
                      // formData.append("packageId", values.packageId);

                      await dispatch(registerEndUser(formData)).unwrap();
                      setIsRegistered(true);
                      resetForm();
                      setTimeout(() => {
                        onHide();
                        toast.success(
                          "Registration successful! Please sign in."
                        );
                      }, 2000);
                    } catch (error) {
                      console.error("Registration error:", error);
                      toast.error(error?.message || "Registration failed");
                    } finally {
                      setSubmitting(false);
                    }
                  }
                }}
              >
                {({ isSubmitting, setFieldValue, values }) => (
                  <Form className="flex flex-col gap-4">
                    {currentStep === 0 && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              First Name
                            </label>
                            <Field
                              type="text"
                              name="firstname"
                              placeholder="Enter First Name"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="firstname"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Last Name
                            </label>
                            <Field
                              type="text"
                              name="lastname"
                              placeholder="Enter Last Name"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="lastname"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Date of Birth
                            </label>
                            <Field
                              type="date"
                              name="dob"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                              max={new Date().toISOString().split("T")[0]}
                            />
                            <ErrorMessage
                              name="dob"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Gender
                            </label>
                            <Field
                              as="select"
                              name="gender"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            >
                              <option value="">Select Gender</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                              <option value="other">Other</option>
                            </Field>
                            <ErrorMessage
                              name="gender"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Blood Group
                            </label>
                            <Field
                              as="select"
                              name="bloodGroup"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            >
                              <option value="">Select Blood Group</option>
                              <option value="A+">A+</option>
                              <option value="A-">A-</option>
                              <option value="B+">B+</option>
                              <option value="B-">B-</option>
                              <option value="AB+">AB+</option>
                              <option value="AB-">AB-</option>
                              <option value="O+">O+</option>
                              <option value="O-">O-</option>
                            </Field>
                            <ErrorMessage
                              name="bloodGroup"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Email
                            </label>
                            <Field
                              type="text"
                              name="email"
                              placeholder="Enter Email"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="email"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Phone Number
                            </label>
                            <Field
                              type="text"
                              name="mobile"
                              placeholder="Enter Phone Number"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="mobile"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Upload Profile Picture
                            </label>
                            <button
                              type="button"
                              className="w-full mt-3 p-2 bg-[#00C70D] text-white rounded-lg text-sm"
                              onClick={() =>
                                document.getElementById("profilePhoto").click()
                              }
                            >
                              Upload
                            </button>
                            <input
                              type="file"
                              id="profilePhoto"
                              className="hidden"
                              onChange={(event) => {
                                setFieldValue(
                                  "profilePhoto",
                                  event.target.files[0]
                                );
                              }}
                            />
                            {values.profilePhoto && (
                              <div className="mt-1 text-xs text-green-600">
                                {values.profilePhoto.name || "File uploaded"}
                              </div>
                            )}
                            <ErrorMessage
                              name="profilePhoto"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {currentStep === 1 && (
                      <>
                        <div>
                          <label className="block text-sm md:text-base font-semibold">
                            Street Address
                          </label>
                          <Field
                            type="text"
                            name="address.address_line"
                            placeholder="Enter Street Address"
                            className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                          />
                          <ErrorMessage
                            name="address.address_line"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              City
                            </label>
                            <Field
                              type="text"
                              name="address.city"
                              placeholder="Enter City"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="address.city"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              District
                            </label>
                            <Field
                              type="text"
                              name="address.district"
                              placeholder="Enter District"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="address.district"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              State
                            </label>
                            <Field
                              type="text"
                              name="address.state"
                              placeholder="Enter State"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="address.state"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Country
                            </label>
                            <Field
                              type="text"
                              name="address.country"
                              placeholder="Enter Country"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="address.country"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Pincode
                            </label>
                            <Field
                              type="text"
                              name="address.pincode"
                              placeholder="Enter Pincode"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                            />
                            <ErrorMessage
                              name="address.pincode"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {/* Commented-out Referral Code step */}
                    {/* {currentStep === 2 && (
                      <>
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Referral Code
                            </label>
                            <Field
                              type="text"
                              name="referralCode"
                              placeholder="Enter Referral Code"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                              onChange={(e) => {
                                setFieldValue("referralCode", e.target.value);
                                debouncedFetchReferredUserData(e.target.value);
                              }}
                            />
                            <ErrorMessage
                              name="referralCode"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                        {referredUserData && (
                          <>
                            <h2 className="text-lg md:text-xl font-medium">
                              Upline Information
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm md:text-base font-semibold">
                                  Full Name
                                </label>
                                <input
                                  type="text"
                                  value={referredUserData.firstname || ""}
                                  readOnly
                                  className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                                />
                              </div>
                              <div>
                                <label className="block text-sm md:text-base font-semibold">
                                  Phone Number
                                </label>
                                <input
                                  type="text"
                                  value={referredUserData.mobile || ""}
                                  readOnly
                                  className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                                />
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    )} */}

                    {/* Commented-out Package Selection step */}
                    {/* {currentStep === 3 && (
                      <div className="mt-4">
                        {packages.length === 0 ? (
                          <p className="text-center text-gray-500 text-sm md:text-base font-medium">
                            No packages available at the moment.
                          </p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                            {packages?.map((pkg) => (
                              <label key={pkg._id} className="block group">
                                <Field
                                  type="radio"
                                  name="packageId"
                                  value={pkg._id}
                                  className="hidden peer"
                                />
                                <div
                                  className={`relative bg-[#F5F5F5] rounded-lg shadow-md p-4 cursor-pointer hover:shadow-lg peer-checked:border-2 peer-checked:border-[#005AD0]`}
                                >
                                  <h3 className="text-lg md:text-xl font-bold text-gray-800 capitalize mb-2">
                                    {pkg.name}
                                  </h3>
                                  <p className="text-gray-600 mb-3 text-xs md:text-sm line-clamp-2">
                                    {pkg.features[0]}{" "}
                                  </p>
                                  <div className="flex justify-between items-center mb-3">
                                    <span className="text-sm font-semibold text-gray-700">
                                      ₹{pkg.price.amount} {pkg.price.currency}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      Valid for {pkg.duration_days} days
                                    </span>
                                  </div>
                                  <div className="mb-3">
                                    <h5>Features</h5>
                                    <div
                                      className="mt-2 overflow-y-auto"
                                      style={{ maxHeight: "200px" }}
                                    >
                                      <ul className="space-y-1">
                                        {pkg?.features?.map(
                                          (feature, index) => (
                                            <li
                                              key={index}
                                              className="text-xs md:text-sm text-gray-600 list-disc pl-5"
                                            >
                                              {feature}
                                            </li>
                                          )
                                        )}
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                        <ErrorMessage
                          name="packageId"
                          component="div"
                          className="text-red-500 text-xs mt-3 text-center font-medium"
                        />
                      </div>
                    )} */}

                    <div className="mt-6 flex justify-center gap-3">
                      {currentStep > 0 && (
                        <button
                          type="button"
                          className="w-28 md:w-36 py-2 bg-gray-300 text-gray-800 font-semibold text-sm md:text-base rounded-md shadow-md hover:bg-gray-400"
                          onClick={() => setCurrentStep((prev) => prev - 1)}
                        >
                          Back
                        </button>
                      )}
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-28 md:w-36 py-2 bg-[#00C70D] text-white font-semibold text-sm md:text-base rounded-md shadow-md flex items-center justify-center ${
                          isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                        }`}
                      >
                        {isSubmitting ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 md:h-5 md:w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Loading...
                          </>
                        ) : currentStep < steps.length - 1 ? (
                          "Continue"
                        ) : (
                          "Register"
                        )}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default EndUserSignUp;
